import { Injectable } from '@angular/core';
import { Actions, createEffect, Effect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { from, of as observableOf, of } from 'rxjs';
import {
  catchError,
  filter,
  map,
  skipWhile,
  switchMap,
  take,
  timeout,
  withLatestFrom,
} from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import * as actions from '../actions/index';
import { getFilesUploading } from '../file-upload/file-upload-selectors';
import { getFileUploadState, getSelectedId, ORGANISATIONS, statistics, TENANTS } from '../reducers';
import {
  AnnouncementDto,
  AnnouncementRequestDto,
  AnnouncementsService,
  AnnouncementUpdateRequestDto,
  ListAnnouncementsRequestParams,
} from '@careapp-group/ng-sdk-v2-new';
import { getAnnouncementsState } from './announcements.selectors';
import { ModelEventDto } from '@careapp-group/ng-sdk-v2-new/model/modelEventDto';

@Injectable()
export class AnnouncementEffects {
  lastFilter: any;
  @Effect()
  getAnnouncementsPaginated = this.actions$.pipe(
    ofType(actions.Announcements.REQUEST_ALL_PAGINATED),
    withLatestFrom(
      this.store.select(getSelectedId(ORGANISATIONS)),
      this.store.select(getSelectedId(TENANTS))
    ),
    switchMap(([action, org, tenantId]) => {
      let requestFilter = this.lastFilter || {};
      if (action.payload !== null) {
        requestFilter = action.payload || {};
        this.lastFilter = requestFilter;
      }
      const filter = {
        ...requestFilter,
      };

      const paginator = (<any>action).paginator;
      return this.announcementsService
        .listAnnouncements({
          organisationId: org ?? undefined,
          tenantId: tenantId ?? undefined,
          limit: filter?.limit,
          skip: filter?.skip,
        } as ListAnnouncementsRequestParams)
        .pipe(
          switchMap((payload) => {
            const resultActions = [];
            if ((<any>action).clear) {
              resultActions.push(actions.Announcements.ClearPaginationAction(paginator));
            }
            resultActions.push(
              actions.Announcements.ResponseSuccessAllPaginatedAction({
                filter,
                paginator,
                items: payload,
              })
            );
            return from(resultActions);
          }),
          catchError((err) => {
            const resultActions = [];
            if ((<any>action).clear) {
              resultActions.push(actions.Announcements.ClearPaginationAction(paginator));
            }
            resultActions.push(
              actions.Announcements.ResponseFailAllPaginatedAction(err, paginator)
            );
            return from(resultActions);
          })
        );
    })
  );

  @Effect()
  getAnnouncementList = this.actions$.pipe(
    ofType(actions.Announcements.REQUEST_ALL),
    withLatestFrom(
      this.store.select(getSelectedId(ORGANISATIONS)),
      this.store.select(getSelectedId(TENANTS))
    ),
    switchMap(([action, orgId, tenantId]) => {
      return this.announcementsService
        .listAnnouncements({ organisationId: orgId ?? undefined, tenantId: tenantId ?? undefined })
        .pipe(
          map((payload: AnnouncementDto[]) =>
            actions.Announcements.ResponseSuccessAllAction(payload)
          ),
          catchError((err) => observableOf(actions.Announcements.ResponseFailAction(err)))
        );
    })
  );

  @Effect()
  getAnnouncement = this.actions$.pipe(
    ofType(actions.Announcements.REQUEST_BY_ID),
    switchMap((action) => {
      return this.announcementsService.getAnnouncement({ id: action.payload.id }).pipe(
        map((payload: AnnouncementDto) => actions.Announcements.ResponseSuccessByIdAction(payload)),
        catchError((err) => observableOf(actions.Announcements.ResponseFailByIdAction(err)))
      );
    })
  );

  @Effect()
  getTotalCountForOrganisation = this.actions$.pipe(
    ofType(actions.Announcements.REQUEST_TOTAL_COUNT),
    withLatestFrom(
      this.store.select(getSelectedId(ORGANISATIONS)),
      this.store.select(statistics.getFilters)
    ),
    filter(([action, orgId, filter]) => !!orgId),
    switchMap(([action, orgId, filter]) =>
      this.announcementsService
        .countAnnouncements({
          organisationId: orgId,
          startDate: filter?.startDate?.toISOString(),
          endDate: filter?.endDate?.toISOString(),
        })
        .pipe(
          map((result) => actions.Announcements.ResponseSuccessTotalCount(result.count)),
          catchError((err) => observableOf(actions.Announcements.ResponseFailTotalCount(err)))
        )
    )
  );

  @Effect()
  createAnnouncement = this.actions$.pipe(
    ofType(actions.Announcements.REQUEST_CREATE),
    switchMap((action) =>
      this.store.select(getFilesUploading).pipe(
        skipWhile((uploading) => uploading),
        take(1),
        withLatestFrom(
          this.store.select(getFileUploadState),
          this.store.select(getSelectedId(ORGANISATIONS))
        ),
        switchMap(([uploading, files, organisationId]) => {
          // Replace the photo ids with server ids
          const { existingFileIds, ...newAnnouncementPayload } = action.payload;
          const fileServerIds = newAnnouncementPayload.fileIds.map((id) => files[id].response.id);
          const organisationIds = action.payload.organisationIds || [organisationId];

          const createRequest: AnnouncementRequestDto = {
            ...newAnnouncementPayload,
            ...{ fileIds: fileServerIds },
            organisationIds,
          };

          return this.announcementsService
            .createAnnouncement({ announcementRequestDto: createRequest })
            .pipe(
              timeout(environment.httpTimeout),
              map((payload: any) => actions.Announcements.ResponseSuccessCreateAction(payload)),
              catchError((err) => of(actions.Announcements.ResponseFailCreateAction(err)))
            );
        })
      )
    )
  );

  @Effect()
  updateAnnouncement = this.actions$.pipe(
    ofType(actions.Announcements.REQUEST_SAVE),
    switchMap((action) =>
      this.store.select(getFilesUploading).pipe(
        skipWhile((uploading) => uploading),
        take(1),
        withLatestFrom(
          this.store.select(getFileUploadState),
          this.store.select(getSelectedId(ORGANISATIONS))
        ),
        switchMap(([uploading, files, organisationId]) => {
          // Replace the photo ids with server ids
          const { existingFileIds, id, ...announcementPayload } = (<any>action).payload.body;
          const fileServerIds = (announcementPayload?.fileIds ?? []).map(
            (id) => files[id].response.id
          );

          // we merge the newly uploaded files with the existing ones
          const allAttachments = Array.from(
            new Set([...(fileServerIds ?? []), ...(existingFileIds ?? [])])
          );
          const organisationIds = announcementPayload?.organisationIds || [organisationId];

          const updateRequest: AnnouncementUpdateRequestDto = {
            ...announcementPayload,
            ...(allAttachments.length ? { fileIds: allAttachments } : {}),
            organisationIds,
          };
          return this.announcementsService
            .updateAnnouncement({
              announcementId: action.payload.id,
              announcementUpdateRequestDto: updateRequest,
            })
            .pipe(
              timeout(environment.httpTimeout),
              map((payload: any) => actions.Announcements.ResponseSuccessSaveAction(payload)),
              catchError((err) => of(actions.Announcements.ResponseFailSaveAction(err)))
            );
        })
      )
    )
  );

  @Effect()
  deleteAnnouncements = this.actions$.pipe(
    ofType(actions.Announcements.REQUEST_DESTROY_BY_ID),
    withLatestFrom(this.store.select(getSelectedId(ORGANISATIONS))),
    switchMap(([action, org]) =>
      this.announcementsService.deleteAnnouncement({ announcementId: action.payload.id }).pipe(
        map((payload: AnnouncementDto) =>
          actions.Announcements.ResponseSuccessDestroyAction({
            id: action.payload.id,
          })
        ),
        catchError((err) => of(actions.Announcements.ResponseFailDestroyAction(err)))
      )
    )
  );

  logView$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.Announcements.requestLogView),
      withLatestFrom(
        this.store.select(getAnnouncementsState),
        this.store.select(getSelectedId(ORGANISATIONS))
      ),

      // If this announcement has not been recently viewed, continue.
      filter(
        ([action, announcementsState, orgId]) =>
          (!!action &&
            !!announcementsState &&
            !(announcementsState.entities[action.announcementId] as any)?.recentlyViewed) ??
          true
      ),
      switchMap(([action, announcementsState, organisationId]) =>
        this.announcementsService
          .logAnnouncementView({
            id: action.announcementId,
            logViewEventMetaDto: {
              organisationId,
            },
          })
          .pipe(
            map((payload: any) => {
              return actions.Announcements.responseSuccessLogView({
                announcementId: action.announcementId,
              });
            }),
            catchError((err) =>
              of(
                actions.Announcements.responseFailLogView({
                  announcementId: action.announcementId,
                  error: err,
                })
              )
            )
          )
      )
    )
  );

  @Effect()
  getAnnouncementHistory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.Announcements.RequestHistory),
      switchMap((action) => {
        return this.announcementsService.getAnnouncementHistory({ id: action.announcementId }).pipe(
          map((payload: ModelEventDto[]) =>
            actions.Announcements.ResponseSuccessHistory({
              announcementId: action.announcementId,
              history: payload,
            })
          ),
          catchError((err) =>
            observableOf(
              actions.Announcements.ResponseFailHistory({
                announcementId: action.announcementId,
                error: err,
              })
            )
          )
        );
      })
    )
  );

  constructor(
    private store: Store<any>,
    private actions$: Actions<actions.CAAction>,
    private announcementsService: AnnouncementsService
  ) {}
}
